import { AlertCircle, Button, Calendar, Card, Check, CheckCircle2, Clock, Eye, EyeOff, Inbox, ListTodo, Moon, Plus, Sparkles, cn } from "@app/ui";

import { formatDuration } from "../../lib/format.js";
import { motion, springs, useReducedMotion } from "../../lib/motion/index.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { CalendarDayIcon } from "../../lib/useDynamicFavicon.js";
import type { DayPreview, DayProgress } from "./agenda.js";

/** From this hour on the hero swaps into "evening review" mode and previews tomorrow. */
const EVENING_HOUR = 18;

interface Props {
  now: Date;
  /** Today's task completion (events excluded). */
  progress: DayProgress;
  eventCount: number;
  overdueCount: number;
  snoozedCount: number;
  /** Total timed minutes planned today (tasks + events, any status). */
  scheduledMin: number;
  /** Tomorrow's glance for the evening line; null hides it. */
  tomorrow: DayPreview | null;
  /** Open undated backlog size — the Inbox chip (clicking opens /inbox). */
  inboxCount: number;
  hideDone: boolean;
  onToggleHideDone: () => void;
  onNew: () => void;
  /** When set, a "Plan my day" button opens the first-fit plan dialog. */
  onPlanDay?: (() => void) | undefined;
  onOpenInbox: () => void;
  /** Scroll the overdue / snoozed section into view. */
  onJumpOverdue: () => void;
  onJumpSnoozed: () => void;
}

/**
 * The Today hero: date icon + time-of-day greeting, an animated completion
 * ring, and a row of colorful stat chips (open/events/done always informative,
 * overdue/snoozed clickable — they scroll to their section).
 */
export function TodayHero({
  now, progress, eventCount, overdueCount, snoozedCount, scheduledMin, tomorrow, inboxCount,
  hideDone, onToggleHideDone, onNew, onPlanDay, onOpenInbox, onJumpOverdue, onJumpSnoozed,
}: Props) {
  const { t } = useI18n();
  const locale = useAppLocale();
  const tf = useTimeFormat();
  const hour = now.getHours();
  const greeting =
    hour < 5 ? t("today.greeting.evening")
    : hour < 12 ? t("today.greeting.morning")
    : hour < 18 ? t("today.greeting.afternoon")
    : t("today.greeting.evening");
  const open = progress.total - progress.done;

  return (
    <Card className="relative overflow-hidden p-4 sm:p-5">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent" />
      <div aria-hidden className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-primary/10 blur-2xl" />

      <div className="relative flex flex-col items-stretch gap-x-4 gap-y-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex min-w-0 w-full items-center gap-3 sm:w-auto sm:flex-1">
          <CalendarDayIcon size={44} />
          <div className="min-w-0">
            <h1 className="break-words text-2xl font-semibold leading-tight sm:truncate">{greeting}</h1>
            <p className="break-words text-sm text-muted-foreground sm:truncate">
              {now.toLocaleDateString(locale, { weekday: "long", month: "long", day: "numeric" })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-2.5">
          {progress.total > 0 && <ProgressRing done={progress.done} total={progress.total} />}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            aria-pressed={hideDone}
            title={hideDone ? t("today.showDone") : t("today.hideDone")}
            aria-label={hideDone ? t("today.showDone") : t("today.hideDone")}
            onClick={onToggleHideDone}
          >
            {hideDone ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          {onPlanDay && (
            <Button
              variant="outline"
              onClick={onPlanDay}
              title={t("today.planDay")}
              aria-label={t("today.planDay")}
            >
              <Sparkles className="h-4 w-4" /> <span className="hidden sm:inline">{t("today.planDay")}</span>
            </Button>
          )}
          <Button className="whitespace-nowrap" onClick={onNew}>
            <Plus className="h-4 w-4" /> {t("common.newTask")}
          </Button>
        </div>
      </div>

      {(progress.total > 0 || eventCount > 0 || overdueCount > 0 || snoozedCount > 0 || scheduledMin > 0 || inboxCount > 0) && (
        <div className="relative mt-3 flex flex-wrap items-center gap-1.5">
          <StatChip icon={ListTodo} label={t("today.chip.tasks")} count={open} className="bg-blue-500/10 text-blue-600 dark:text-blue-300" />
          <StatChip icon={Calendar} label={t("today.chip.events")} count={eventCount} className="bg-violet-500/10 text-violet-600 dark:text-violet-300" />
          <StatChip icon={Clock} label={t("today.chip.scheduled")} count={scheduledMin > 0 ? formatDuration(scheduledMin) : 0} className="bg-teal-500/10 text-teal-600 dark:text-teal-300" />
          <StatChip icon={CheckCircle2} label={t("today.chip.done")} count={progress.done} className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" />
          <StatChip icon={AlertCircle} label={t("today.overdue")} count={overdueCount} className="bg-red-500/10 text-red-600 dark:text-red-300" onClick={onJumpOverdue} />
          <StatChip icon={Moon} label={t("today.snoozed")} count={snoozedCount} className="bg-purple-500/10 text-purple-600 dark:text-purple-300" onClick={onJumpSnoozed} />
          <StatChip icon={Inbox} label={t("today.chip.inbox")} count={inboxCount} className="bg-indigo-500/10 text-indigo-600 dark:text-indigo-300" onClick={onOpenInbox} />
        </div>
      )}

      {hour >= EVENING_HOUR && tomorrow && (
        <p className="relative mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary/70" aria-hidden />
          {tomorrow.open === 0
            ? t("today.tomorrowFree")
            : tomorrow.firstStart
              ? t("today.tomorrowPreview", { count: tomorrow.open, time: tf.hmDate(tomorrow.firstStart) })
              : t("today.tomorrowPreviewNoTime", { count: tomorrow.open })}
        </p>
      )}
    </Card>
  );
}

/** Animated completion ring; shows done/total in the center, a check at 100%. */
function ProgressRing({ done, total }: { done: number; total: number }) {
  const reduce = useReducedMotion();
  const pct = total === 0 ? 0 : done / total;
  const size = 48;
  const r = 20;
  const c = 2 * Math.PI * r;
  const complete = total > 0 && done === total;

  return (
    <div
      className="relative grid place-items-center"
      style={{ width: size, height: size }}
      role={total > 0 ? "progressbar" : undefined}
      aria-valuemin={total > 0 ? 0 : undefined}
      aria-valuemax={total > 0 ? total : undefined}
      aria-valuenow={total > 0 ? done : undefined}
      aria-label={total > 0 ? `${done}/${total}` : undefined}
      title={`${done}/${total}`}
    >
      <svg aria-hidden width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <defs>
          <linearGradient id="today-ring" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="100%" stopColor="#22c55e" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={5} className="stroke-muted-foreground/15" />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={5}
          strokeLinecap="round"
          stroke="url(#today-ring)"
          strokeDasharray={c}
          initial={reduce ? false : { strokeDashoffset: c }}
          animate={{ strokeDashoffset: c * (1 - pct) }}
          transition={reduce ? { duration: 0 } : springs.smooth}
        />
      </svg>
      <span className="absolute text-[calc(10px*var(--app-text-scale))] font-semibold tabular-nums">
        {complete ? <Check aria-hidden className="h-4 w-4 text-emerald-500" /> : `${done}/${total}`}
      </span>
    </div>
  );
}

function StatChip({ icon: Icon, label, count, className, onClick }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /** Number, or a preformatted string (e.g. a duration). Falsy hides the chip. */
  count: number | string;
  className: string;
  onClick?: () => void;
}) {
  if (!count) return null;
  const inner = (
    <>
      <Icon className="h-3.5 w-3.5" />
      {label}
      <span className="font-semibold tabular-nums">{count}</span>
    </>
  );
  const base = "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium";
  if (!onClick) return <span className={cn(base, className)}>{inner}</span>;
  return (
    <button type="button" className={cn(base, className, "transition-transform hover:scale-[1.05] active:scale-95 motion-reduce:transform-none motion-reduce:transition-none")} onClick={onClick}>
      {inner}
    </button>
  );
}
